/**
 * Admin Users Page
 *
 * User management interface for administrators.
 * Allows viewing, approving, disabling, and promoting users.
 */

import React, { useEffect, useState } from 'react';
import { Navbar, Card, Button, Modal } from '../components';
import { userApi, getErrorMessage } from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { User, PaginatedResponse } from '../types';

interface ModalState {
  isOpen: boolean;
  title: string;
  message: string;
  variant: 'info' | 'warning' | 'danger';
  onConfirm?: () => void;
}

export const AdminUsersPage: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    pages: 1,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('');

  // Modal state for confirmations
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
  });

  const fetchUsers = async (page = 1) => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const params: any = { page, limit: 20 };
      if (filterStatus) params.status = filterStatus;
      if (filterRole) params.role = filterRole;

      const response: PaginatedResponse<User> = await userApi.getUsers(params);
      setUsers(response.items);
      setPagination({
        total: response.total,
        page: response.page,
        limit: response.limit,
        pages: Math.ceil(response.total / response.limit),
      });
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filterStatus, filterRole]);

  const handleApprove = (id: string, email: string) => {
    setModalState({
      isOpen: true,
      title: 'Approve User',
      message: `Approve user ${email}?\n\nThey will gain access to all services.`,
      variant: 'info',
      onConfirm: async () => {
        try {
          await userApi.approveUser(id);
          fetchUsers(pagination.page);
        } catch (error) {
          setErrorMessage(getErrorMessage(error));
        }
      },
    });
  };

  const handleDisable = (id: string, email: string) => {
    setModalState({
      isOpen: true,
      title: 'Disable User',
      message: `Disable user ${email}?\n\nThey will lose access to all services immediately.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await userApi.disableUser(id);
          fetchUsers(pagination.page);
        } catch (error) {
          setErrorMessage(getErrorMessage(error));
        }
      },
    });
  };

  const handleEnable = (id: string, email: string) => {
    setModalState({
      isOpen: true,
      title: 'Enable User',
      message: `Re-enable user ${email}?\n\nThey will regain access to services.`,
      variant: 'info',
      onConfirm: async () => {
        try {
          await userApi.enableUser(id);
          fetchUsers(pagination.page);
        } catch (error) {
          setErrorMessage(getErrorMessage(error));
        }
      },
    });
  };

  const handlePromote = (id: string, email: string) => {
    setModalState({
      isOpen: true,
      title: 'Promote to Admin',
      message: `Promote ${email} to administrator?\n\nThey will have full access to all features including user management.`,
      variant: 'warning',
      onConfirm: async () => {
        try {
          await userApi.promoteUser(id);
          fetchUsers(pagination.page);
        } catch (error) {
          setErrorMessage(getErrorMessage(error));
        }
      },
    });
  };

  const handleDelete = (id: string, email: string) => {
    setModalState({
      isOpen: true,
      title: 'Delete User',
      message: `Permanently delete user ${email}?\n\nThis action cannot be undone. All user data will be removed.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await userApi.deleteUser(id);
          fetchUsers(pagination.page);
        } catch (error) {
          setErrorMessage(getErrorMessage(error));
        }
      },
    });
  };

  const pageStyles: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: 'var(--bg-primary)',
  };

  const containerStyles: React.CSSProperties = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '40px 20px',
  };

  const titleStyles: React.CSSProperties = {
    fontSize: '32px',
    fontWeight: 600,
    marginBottom: '32px',
    color: 'var(--text-primary)',
  };

  const filtersStyles: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
  };

  const selectStyles: React.CSSProperties = {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
  };

  const tableContainerStyles: React.CSSProperties = {
    overflowX: 'auto',
  };

  const tableStyles: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'var(--bg-card)',
  };

  const thStyles: React.CSSProperties = {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid var(--border-color)',
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
  };

  const tdStyles: React.CSSProperties = {
    padding: '12px',
    borderBottom: '1px solid var(--border-light)',
    fontSize: '14px',
    color: 'var(--text-primary)',
  };

  const avatarStyles: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover',
  };

  const badgeStyles = (color: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: color,
    color: '#fff',
  });

  const actionButtonStyles: React.CSSProperties = {
    padding: '6px 12px',
    fontSize: '12px',
    marginRight: '8px',
  };

  const paginationStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '24px',
  };

  return (
    <div style={pageStyles}>
      <Navbar />

      <div style={containerStyles}>
        <h1 style={titleStyles}>User Management</h1>

        <Card>
          {/* Filters */}
          <div style={filtersStyles}>
            <select
              style={selectStyles}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="disabled">Disabled</option>
            </select>

            <select
              style={selectStyles}
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>

            <Button variant="secondary" onClick={() => {
              setFilterStatus('');
              setFilterRole('');
            }}>
              Clear Filters
            </Button>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div style={{ padding: '12px', backgroundColor: '#fce4ec', color: '#c62828', borderRadius: '8px', marginBottom: '16px', border: '1px solid #f8bbd0' }}>
              {errorMessage}
            </div>
          )}

          {/* Loading */}
          {isLoading && <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>}

          {/* Users Table */}
          {!isLoading && users.length > 0 && (
            <>
              <div style={tableContainerStyles}>
                <table style={tableStyles}>
                  <thead>
                    <tr>
                      <th style={thStyles}>User</th>
                      <th style={thStyles}>Email</th>
                      <th style={thStyles}>Role</th>
                      <th style={thStyles}>Status</th>
                      <th style={thStyles}>Joined</th>
                      <th style={thStyles}>Last Login</th>
                      <th style={thStyles}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td style={tdStyles}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {user.photo && (
                              <img src={user.photo} alt={user.name} style={avatarStyles} />
                            )}
                            <span>{user.name}</span>
                          </div>
                        </td>
                        <td style={tdStyles}>{user.email}</td>
                        <td style={tdStyles}>
                          <span style={badgeStyles(user.role === 'admin' ? '#4285f4' : '#5f6368')}>
                            {user.role}
                          </span>
                        </td>
                        <td style={tdStyles}>
                          <span style={badgeStyles(
                            user.status === 'active' ? '#34a853' :
                            user.status === 'pending' ? '#fbbc05' : '#ea4335'
                          )}>
                            {user.status}
                          </span>
                        </td>
                        <td style={tdStyles}>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td style={tdStyles}>
                          {user.lastLoginAt
                            ? new Date(user.lastLoginAt).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td style={tdStyles}>
                          {user.status === 'pending' && (
                            <Button
                              variant="primary"
                              style={actionButtonStyles}
                              onClick={() => handleApprove(user.id, user.email)}
                            >
                              Approve
                            </Button>
                          )}
                          {user.status === 'active' && user.id !== currentUser?.id && (
                            <Button
                              variant="danger"
                              style={actionButtonStyles}
                              onClick={() => handleDisable(user.id, user.email)}
                            >
                              Disable
                            </Button>
                          )}
                          {user.status === 'disabled' && (
                            <Button
                              variant="primary"
                              style={actionButtonStyles}
                              onClick={() => handleEnable(user.id, user.email)}
                            >
                              Enable
                            </Button>
                          )}
                          {user.role === 'user' && user.status === 'active' && (
                            <Button
                              variant="primary"
                              style={actionButtonStyles}
                              onClick={() => handlePromote(user.id, user.email)}
                            >
                              Promote
                            </Button>
                          )}
                          {user.id !== currentUser?.id && (
                            <Button
                              variant="danger"
                              style={actionButtonStyles}
                              onClick={() => handleDelete(user.id, user.email)}
                            >
                              Delete
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div style={paginationStyles}>
                <Button
                  variant="secondary"
                  disabled={pagination.page === 1}
                  onClick={() => fetchUsers(pagination.page - 1)}
                >
                  Previous
                </Button>
                <span>
                  Page {pagination.page} of {pagination.pages} ({pagination.total} users)
                </span>
                <Button
                  variant="secondary"
                  disabled={pagination.page === pagination.pages}
                  onClick={() => fetchUsers(pagination.page + 1)}
                >
                  Next
                </Button>
              </div>
            </>
          )}

          {/* No Users */}
          {!isLoading && users.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              No users found
            </div>
          )}
        </Card>
      </div>

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        variant={modalState.variant}
        onConfirm={modalState.onConfirm}
        confirmText="Confirm"
        cancelText="Cancel"
      />
    </div>
  );
};
